import Vue from 'vue';
import Vuex from 'vuex';
import moment from 'moment';
Vue.use(Vuex);

import { getField, updateField } from 'vuex-map-fields';
import { debounce, delay, has } from 'lodash';
import { stateMerge } from 'vue-object-merge';

import deduplication_manager from '@/js/utilities/deduplication-manager.js';
import PubNubVue from 'pubnub-vue';
import uuid from '@/js/utilities/uuid-generator.js';

import router from '@/js/router.js';

var last_mutation_type, last_mutation_path;

const application = {
	namespaced: true,
	state: {
		section_status: null
	},
	mutations: {
		updateField,
		importState(state, application){
			stateMerge(state, application);
		}
	},
	getters:{
		getField
	},
	actions: {
		initSectionStatus({ commit }, payload ){
			commit('updateField', { path: 'section_status', value: payload });
		},
		setSectionStatus({ commit, rootState }, status){
			const path_array = rootState.route.path.split('/');
			const section_name = (path_array[path_array.length - 1]).replace(/-/g, '_');
			commit('updateField', { path: `section_status.${section_name}`, value: status });
		},
		async getStatus( vuex, application_hash){
			const { data } = await this._vm.$axios.get(`${process.env.VUE_APP_API_PATH}/verify.php?hash=${application_hash}`);
			return data.status;
		},
		async pullApplication({ rootState }){
			const application_hash = rootState.route.params.application_hash;
			const { data } =
			await this._vm.$axios.get(`${process.env.VUE_APP_API_PATH}/pull-application.php?hash=${application_hash}`);
			return data;
		},
		async mergeApplication({ commit }, application){
			try{
				commit('importState', application.data);
				commit('updateField', {
					path: 'submitted_at',
					value: application.submitted_at },
					{ root: true }
				);
			} catch(error) {
				this._vm.$notify.error({
					title: 'Unable to Retrieve Application',
					message: 'We were unable to retrieve your application. Please contact Greenbelt.'
				});
				throw error; // Bubbling up error
			}
		},
		async saveApplication({ state, rootState }){
			let notification, notification_type;
			const data = {
				application_hash: rootState.route.params.application_hash,
				application: state
			}
			try{
				await this._vm.$axios.post(`${process.env.VUE_APP_API_PATH}/save.php`, data);
				notification_type = 'success';
				notification = {
					title: 'Save for Later',
					message: 'Successfully saved your application for later.'
				};
			} catch(error){
				notification_type = 'error';
				notification = {
					title: 'Save for Later',
					message: 'Unable to save your application.'
				};
			} finally {
				this._vm.$notify[notification_type](notification);
			}
		},
		async patchApplication({ commit, rootState }, { path, value } ){
			const data = {
				application_hash: rootState.route.params.application_hash,
				path,
				application_chunk: value
			}
			try{
				commit('updateField', { path: 'saving_application', value: true }, { root: true });
				await this._vm.$axios.post(`${process.env.VUE_APP_API_PATH}/patch.php`, data);
				commit('updateField', { path: 'last_saved', value: new Date() }, { root: true });
			} catch(error) {
				console.error(error);
				this._vm.$notify.error({
					title: 'Save for Later',
					message: 'Unable to save your application.'
				});
			} finally {
				delay(()=>{commit('updateField', { path: 'saving_application', value: false }, { root: true })}, 500);

			}
		},
		async submitApplication({ state, rootState }){
			try {
				const data = {
					application_hash: rootState.route.params.application_hash,
					application: state
				}
				await this._vm.$axios.post(`${process.env.VUE_APP_API_PATH}/submit.php`, data);
				router.push( 'exit' );
			} catch(error){
				console.error(error)
				this._vm.$notify.error({
					title: 'Unable to Submit',
					message: 'We were unable to submit your application. Please contact Greenbelt.'
				});
			}
		},
		async subscribeToChannel({ rootState, dispatch }){
			console.log('subscribing', rootState.route.params.application_hash);
			await this._vm.$pnSubscribe({
				channels: [rootState.route.params.application_hash],
				withPresence: true
			});

			// Adding server-side filter for own requests
			const pubnub = PubNubVue.getInstance();
			pubnub.setFilterExpression(`uuid != '${uuid}'`);

			this._vm.$pnGetMessage(rootState.route.params.application_hash, event => dispatch('reactToMessage', event));
			this._vm.$pnGetPresence(rootState.route.params.application_hash, event => dispatch('reactToPresence', event));
		},
		async unsubscribeFromChannel({ rootState }){
			console.log('unsubscribing');
			await this._vm.$pnUnsubscribe({
				channels: [rootState.route.params.application_hash],
			});
		},
		reactToMessage({ commit }, event){
			if(event.message.mutation !== undefined && event.publisher !== uuid){
				// console.log('merging', event);
				const { message: { mutation }} = event;
				deduplication_manager.addMutation(mutation);
				const { type, payload } = mutation;
				commit(type, payload, { root: true });
			} else {
				console.warn('filtering out own message')
			}
		},
		reactToPresence({ commit }, { occupancy }){
			commit('updateField', { path: 'sessions_active', value: occupancy }, { root: true });
		},
		async broadcastUpdate({ rootState }, mutation){
			// if(window.pubnub_occupancy > 1){
			await this._vm.$pnPublish({
				channel : rootState.route.params.application_hash,
				message : {	mutation },
				meta: { uuid }
			});
			// } else {
			// console.log('no one to broadcast to.');
			// }
		},
		debouncePatch: debounce( async ({ dispatch }, mutation) => {
			await dispatch('patchAll', mutation);
		}, 500),
		trafficControl({ dispatch }, mutation){
			// console.log('traffic', mutation, last_mutation_type, last_mutation_path)
			if(last_mutation_type === mutation.type && (!has(mutation, 'payload.path') || last_mutation_path === mutation.payload.path)){
				// Debounce it
				// console.log('debounce');
				dispatch('debouncePatch', mutation);
			} else {
				// Do immediately
				// console.log('immediate');
				dispatch('patchAll', mutation);
			}
			last_mutation_type = mutation.type;
			last_mutation_path = has(mutation, 'payload.path') ? mutation.payload.path : null;

		},
		async patchAll({ dispatch }, mutation){
			try{
				await dispatch('broadcastUpdate', mutation);
				const transformed_mutation = await dispatch('transformMutation', mutation);
				await dispatch('patchApplication', transformed_mutation);
			} catch(error){
				console.warn('Mutation not supported for patch');
			}
		},
		transformMutation(vuex, mutation){
			const { type, payload: { path, value } } = mutation;
			const split_types = type.split('/');
			const type_path = split_types.slice(1, split_types.length - 1); // split and purge mutation name at end
			const payload_path = path.split('.');
			const final_path = [ ...type_path, ...payload_path ];
			return { path: final_path, value };
		}
	}
}

export default new Vuex.Store({
	strict: (process.env.NODE_ENV !== 'production'), // Enforce store in development only,
	state: {
		deadline: null,
		submitted_at: null,
		sessions_active: null,
		saving_application: false,
		last_saved: null,
		full_screen_loading: true
	},
	modules: {
		application
	},
	getters: {
		getField
	},
	mutations: {
		updateField
	},
	actions: {
		async fetchDeadline({ commit }){
			const { data } = await this._vm.$axios.get(`${process.env.VUE_APP_API_PATH}/form-configs.php`);
			console.log('data', data.funding_request);
			const retrieved_date = new moment(data.funding_request.deadline)

			commit('updateField', { path: 'deadline', value: retrieved_date });
		}
	}
})
